// Import Swiper styles
import "swiper/css";
import "swiper/css/a11y";
import "swiper/css/navigation";
import "../../theme/SwiperOverrides.css";
import React, { useState, useRef, useEffect } from "react";
import { A11y, Navigation, Virtual } from "swiper/modules";
import { Swiper, SwiperSlide } from "swiper/react";
import { Colours } from "../../utils/colours";
import { Button } from "../common";
import * as styles from "./BodyCondition.module.scss";

import type { LottieRefCurrentProps } from "lottie-light-react";
import weightSliderAnitmation from "../../static/lottie/DogsTrust_weightSlider_001.json";
import { ClientSideLottie } from "../ClientSideLottie";
import { Arrow } from "../common/Icons";

export const BodyCondition: React.FC = () => {
	const [swiper, setSwiper] = useState(null);
	const [currentSlide, setCurrentSlide] = useState(0);
	const [initialLoad, setInitialLoad] = useState(true);

	const lottieRef = useRef<LottieRefCurrentProps>(null);
	const lottieRefCurrent = lottieRef?.current;
	const activeIndex = swiper?.activeIndex;

	useEffect(() => {
		if (lottieRefCurrent && initialLoad) {
			lottieRef.current.playSegments([0, 20], true);
		}
	}, [lottieRefCurrent, initialLoad]);

	const playSegment = (start: number, end: number) => {
		if (lottieRef.current) {
			lottieRef.current.setSpeed(3);
			lottieRef.current.playSegments([start, end], true);
		}
	};

	return (
		<div className={styles.swiperwrapper}>
			<Swiper
				className={styles.swiper}
				modules={[Virtual, Navigation, A11y]}
				edgeSwipeThreshold={10}
				threshold={0}
				grabCursor={true}
				draggable={false}
				resistance={false}
				autoplay={false}
				noSwiping
				allowTouchMove={false}
				onSwiper={(swiper) => setSwiper(swiper)}
				onSlideChange={(swiper) => setCurrentSlide(swiper.activeIndex)}
				initialSlide={0}
				centerInsufficientSlides={true}
			>
				<SwiperSlide className={styles.cardslide}>
					<div>
						<h3>Signs your dog may be under the idea body condition</h3>
						<ul>
							<li>
								<span>
									Ribs, vertebrae (spine) and pelvic (hip) bones are prominent
									or visible and very easy to feel
								</span>
							</li>
							<li>
								<span>Not able to feel and fat layer over the ribs</span>
							</li>
							<li>
								<span>Possible loss of muscle mass</span>
							</li>
							<li>
								<span>Obviously thin waist</span>
							</li>
						</ul>
					</div>
				</SwiperSlide>
				<SwiperSlide>
					<div>
						<h3>Ideal body condition</h3>
						<ul>
							<li>
								<span>Ribs can be felt easily</span>
							</li>
							<li>
								<span>Minimal or thin layer of fat covering the ribs</span>
							</li>
							<li>
								<span>
									When viewed from above, the waist is obviously narrower that
									the rest of the body
								</span>
							</li>
							<li>
								<span>
									When viewed from the side, the belly tucks in rather than
									being rounded
								</span>
							</li>
						</ul>
					</div>
				</SwiperSlide>
				<SwiperSlide>
					<div>
						<h3>Signs your dog may be over the ideal body condition</h3>
						<ul>
							<li>
								<span>Ribs are hard or impossible to feel under body fat</span>
							</li>
							<li>
								<span>Waist is not obvious when viewed from above</span>
							</li>
							<li>
								<span>
									Fat deposits at the base of the tail, the neck and on their
									legs
								</span>
							</li>
							<li>
								<span>Rounded abdomen</span>
							</li>
						</ul>
					</div>
				</SwiperSlide>
			</Swiper>
			<div className={styles.animationSlider}>
				<button
					className={styles.largepaginationcontrol}
					type="button"
					aria-label="Go back"
					disabled={currentSlide === 0}
					onClick={() => {
						setInitialLoad(false);
						if (activeIndex === 2) {
							playSegment(100, 70);
						} else if (activeIndex === 1) {
							playSegment(70, 20);
						}
						activeIndex === 2 ? swiper?.slideTo(1) : swiper?.slideTo(0);
					}}
				>
					<Arrow
						fill={Colours.MIDNIGHT}
						withBorder={false}
						width="24px"
						height="24px"
					/>
				</button>

				<ClientSideLottie
					animationData={weightSliderAnitmation}
					ariaHidden={true}
					loop={false}
					autoPlay={false}
					lottieRef={lottieRef}
					style={{ height: 200 }}
				/>
				<button
					className={styles.largepaginationcontrol}
					type="button"
					aria-label="Go forward"
					disabled={currentSlide === 2}
					onClick={() => {
						setInitialLoad(false);
						if (activeIndex === 0) {
							playSegment(20, 70);
						} else if (activeIndex === 1) {
							playSegment(70, 100);
						}
						activeIndex === 0 ? swiper?.slideTo(1) : swiper?.slideTo(2);
					}}
				>
					<Arrow
						fill={Colours.MIDNIGHT}
						withBorder={false}
						width="24px"
						height="24px"
						rotation={-180}
					/>
				</button>
			</div>
			<div className={styles.cardslideCondition}>
				<Button
					onClick={() => {
						setInitialLoad(false);
						if (activeIndex === 1) {
							playSegment(70, 20);
						} else if (activeIndex === 2) {
							playSegment(100, 20);
						}
						swiper?.slideTo(0);
					}}
					variant="reverseprimary"
					size="small"
					disabled={currentSlide === 0}
				>
					Under
				</Button>
				<Button
					onClick={() => {
						setInitialLoad(false);
						if (activeIndex === 0) {
							playSegment(20, 70);
						} else if (activeIndex === 2) {
							playSegment(100, 70);
						}
						swiper?.slideTo(1);
					}}
					variant="reverseprimary"
					size="small"
					disabled={currentSlide === 1}
				>
					Ideal
				</Button>
				<Button
					onClick={() => {
						setInitialLoad(false);
						if (activeIndex === 0) {
							playSegment(20, 100);
						} else if (activeIndex === 1) {
							playSegment(70, 100);
						}
						swiper?.slideTo(2);
					}}
					variant="reverseprimary"
					size="small"
					disabled={currentSlide === 2}
				>
					Over
				</Button>
			</div>
		</div>
	);
};

export default BodyCondition;
